/**
 * uper Website Widget loader (~5KB IIFE).
 *
 * Usage — customer embeds this on any page:
 *   <script async src="https://app.uper.pt/widget.js" data-uper-channel="CHN_ULID"></script>
 *
 * Optional overrides on the same tag (rarely needed):
 *   data-uper-app="https://custom.app.origin"   iframe/static origin (defaults to script origin)
 *   data-uper-api="https://custom.api/api/v1"   API base URL (defaults to VITE_API_BASE_URL baked in at build)
 *
 * The loader fetches {API}/public/widget/{channel}/config, draws a floating bubble
 * with the configured color/position, and lazily mounts an iframe pointing to
 * {APP}/w/{channel} on click.
 */

type WidgetConfig = {
  color?: string;
  position?: "left" | "right";
};

(function () {
  const script = (document.currentScript ?? document.querySelector("script[data-uper-channel]")) as
    | HTMLScriptElement
    | null;
  if (!script) return;

  const channel = script.dataset.uperChannel;
  if (!channel) {
    console.warn("[uper-widget] missing data-uper-channel");
    return;
  }

  // Origin serving the iframe / static assets (app.uper.pt in prod).
  const appOrigin =
    script.dataset.uperApp ??
    (() => {
      try {
        return new URL(script.src).origin;
      } catch {
        return "";
      }
    })();

  // API base URL. Baked in at build time from VITE_API_BASE_URL so the loader
  // knows where the backend lives (secure.uper.pt) — the script itself is
  // served from a different origin (app.uper.pt) which has no API.
  // Allow override via `data-uper-api="https://example.com/api/v1"` for staging.
  const apiBase = ((script.dataset.uperApi ??
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    "") as string).replace(/\/$/, "");

  if (!appOrigin || !apiBase) {
    console.warn("[uper-widget] cannot resolve app origin or api base");
    return;
  }

  const state: { open: boolean; iframe: HTMLIFrameElement | null; config: WidgetConfig } = {
    open: false,
    iframe: null,
    config: {},
  };

  fetch(`${apiBase}/public/widget/${encodeURIComponent(channel)}/config`, {
    credentials: "omit",
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`config ${r.status}`))))
    .then((res) => {
      state.config = (res?.data?.config ?? {}) as WidgetConfig;
      renderBubble();
    })
    .catch((e) => console.warn("[uper-widget] config failed", e));

  function renderBubble() {
    if (document.getElementById("uper-widget-bubble")) return;

    const color = state.config.color || "#4f46e5";
    const isLeft = state.config.position === "left";
    const btn = document.createElement("div");
    btn.id = "uper-widget-bubble";
    btn.setAttribute("role", "button");
    btn.setAttribute("aria-label", "Abrir chat");
    btn.style.cssText = [
      "position:fixed",
      "bottom:20px",
      isLeft ? "left:20px" : "right:20px",
      "width:60px",
      "height:60px",
      "border-radius:50%",
      `background:${color}`,
      "cursor:pointer",
      "z-index:2147483647",
      "box-shadow:0 4px 14px rgba(0,0,0,0.18)",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "color:white",
      "font-size:26px",
      "font-family:system-ui,sans-serif",
      "transition:transform .15s ease",
      "user-select:none",
    ].join(";");
    btn.textContent = "💬";
    btn.onmouseenter = () => (btn.style.transform = "scale(1.05)");
    btn.onmouseleave = () => (btn.style.transform = "scale(1)");
    btn.onclick = toggle;
    document.body.appendChild(btn);
  }

  function toggle() {
    if (state.open) {
      state.iframe?.remove();
      state.iframe = null;
      state.open = false;
      return;
    }
    const iframe = document.createElement("iframe");
    iframe.src = `${appOrigin}/w/${encodeURIComponent(channel)}`;
    iframe.setAttribute("title", "Chat");
    iframe.setAttribute(
      "sandbox",
      "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox",
    );
    const isLeft = state.config.position === "left";
    iframe.style.cssText = [
      "position:fixed",
      "bottom:95px",
      isLeft ? "left:20px" : "right:20px",
      "width:380px",
      "height:600px",
      "max-width:calc(100vw - 40px)",
      "max-height:calc(100vh - 120px)",
      "border:0",
      "border-radius:12px",
      "box-shadow:0 12px 40px rgba(0,0,0,0.22)",
      "z-index:2147483646",
      "background:white",
    ].join(";");
    document.body.appendChild(iframe);
    state.iframe = iframe;
    state.open = true;
  }
})();
